import React, { useEffect, useState } from "react";
import { Document, Page } from "@react-pdf/renderer";
import { useSearchParams } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";
import { viewSubmittionDetails } from "../../../../services/scholarshipService";
import { isDate } from "../../../../util/Flag";
import dayjs from "dayjs";
import SmartFieldRenderer from "../../../form/SmartFieldRenderer";

const Section = ({ icon: Icon, title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="shadow-md border border-gray-200">
      <div className="space-y-4 p-5">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setOpen(!open)}
        >
          <div className="flex items-center gap-3 text-xl font-semibold text-gray-800">
            <i className={Icon} />
            {title}
          </div>
          {open ? (
            <i className="fa-regular fa-chevron-up" />
          ) : (
            <i className="fa-regular fa-chevron-down" />
          )}
        </div>
        <hr className="my-2" />
        {open && children}
      </div>
    </div>
  );
};

const PdfViewer = ({ fileUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const onLoadSuccess = ({ numPages }) => setNumPages(numPages);

  return (
    <div className="border rounded-md overflow-hidden shadow-inner bg-gray-50 p-2 max-w-full">
      <Document file={fileUrl} onLoadSuccess={onLoadSuccess}>
        <Page
          pageNumber={pageNumber}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          className="my-2 border rounded-sm shadow-sm"
        />
      </Document>
      <div className="flex justify-between items-center mt-2">
        <button
          onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
          disabled={pageNumber === 1}
          className="px-4 py-2 bg-primary text-white rounded-md"
        >
          Previous
        </button>
        <span className="text-sm">
          Page {pageNumber} of {numPages}
        </span>
        <button
          onClick={() => setPageNumber((prev) => Math.min(prev + 1, numPages))}
          disabled={pageNumber === numPages}
          className="px-4 py-2 bg-primary text-white rounded-md"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const ViewSubmissionDetails = () => {
  const [searchParams] = useSearchParams();
  const scholarshipId = searchParams.get("scholarshipId");
  const [detailsData, setDetailsData] = useState(null);

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["viewScholarship"],
    fetchFunction: async () => {
      const res = await viewSubmittionDetails({
        id: scholarshipId,
      });

      return res?.data;
    },
  });

  useEffect(() => {
    if (data) {
      const groupedData = data?.dynamicFieldDetails?.reduce((acc, item) => {
        const group = item.groupCode;
        if (!acc[group]) acc[group] = [];
        acc[group].push({
          label: item.label,
          value: isDate(item.valueOfDynamicFormDetails.value)
            ? dayjs(item.valueOfDynamicFormDetails.value).format("DD MMM, YYYY")
            : item.valueOfDynamicFormDetails.value || "-",
          type: item.type,
          fieldId: item._id,
        });
        return acc;
      }, {});
      setDetailsData({ ...data, dynamicData: groupedData });
    }
  }, [data]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <h2 className="text-3xl font-bold mb-6 text-center text-primary">
        Submission Details
      </h2>

      {detailsData?.dynamicData?.Student_Personal_Information?.length > 0 && (
        <Section
          icon={"fa-regular fa-user"}
          title="Student Personal Information"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
            {detailsData?.dynamicData?.Student_Personal_Information.map(
              (item, index) => (
                <div key={index}>
                  <strong>{item?.label}: </strong>
                  {item?.value?.charAt(0)?.toUpperCase() +
                    item?.value?.slice(1)}
                </div>
              )
            )}
          </div>
        </Section>
      )}

      {detailsData?.dynamicData?.Parent_Guardian_Information?.length > 0 && (
        <Section
          icon={"fa-regular fa-shield-check"}
          title="Parent/Guardian Information"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
            {detailsData?.dynamicData?.Parent_Guardian_Information.map(
              (item, index) => (
                <div key={index}>
                  <strong>{item?.label}: </strong>
                  {item?.value?.charAt(0)?.toUpperCase() +
                    item?.value?.slice(1)}
                </div>
              )
            )}
          </div>
        </Section>
      )}

      {detailsData?.dynamicData?.School_Information?.length > 0 && (
        <Section icon={"fa-regular fa-school"} title="School Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
            {detailsData?.dynamicData?.School_Information.map((item, index) => (
              <div key={index}>
                <strong>{item?.label}: </strong>
                {item?.value?.charAt(0)?.toUpperCase() + item?.value?.slice(1)}
              </div>
            ))}
          </div>
        </Section>
      )}

      {detailsData?.dynamicData?.School_Payment_Information?.length > 0 && (
        <Section
          icon={"fa-regular fa-credit-card-front"}
          title="School Payment Information"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
            {detailsData?.dynamicData?.School_Payment_Information.map(
              (item, index) => (
                <div key={index}>
                  <strong>{item?.label}: </strong>
                  {item?.value?.charAt(0)?.toUpperCase() +
                    item?.value?.slice(1)}
                </div>
              )
            )}
          </div>
        </Section>
      )}

      {detailsData?.dynamicData?.Supporting_Documents?.length > 0 && (
        <Section icon={"fa-regular fa-file-pdf"} title="Supporting Documents">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
            {detailsData?.dynamicData?.Supporting_Documents.map(
              (item, index) => (
                <SmartFieldRenderer
                  key={index}
                  label={item?.label}
                  value={item?.value}
                />
              )
            )}
          </div>
        </Section>
      )}

      {detailsData?.dynamicData?.Certification_and_Authorization?.length >
        0 && (
        <Section icon={"fa-regular fa-shield-check"} title="Consent Document">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
            <SmartFieldRenderer
              label={
                detailsData?.dynamicData?.Certification_and_Authorization[0]
                  ?.label
              }
              value={
                detailsData?.dynamicData?.Certification_and_Authorization[0]
                  ?.value
              }
            />
          </div>
        </Section>
      )}

      {/* <Section icon={"fa-regular fa-location-dot"} title="IP & Location Info">
        <PdfViewer
          fileUrl={
            ""
            // data.consentPdfUrl
          }
        />
      </Section> */}
    </div>
  );
};

export default ViewSubmissionDetails;
